import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';

import { filter, map } from 'rxjs/operators';

import { AppIconComponent } from '@components/@fec/app-icon.component';
import {
  DropdownMenuComponent,
  DropdownMenuItemType,
} from '@components/@fec/dropdown-menu.component';
import { TextInputComponent } from '@components/@fec/text-input.component';
import {
  aiHelpProjectId,
  ALL_AI_HELP_QUESTIONS,
  AI_HELP_CATEGORIES,
  resolveAiHelpPageContext,
} from '@components/ai-help/ai-help-data';
import {
  AiHelpAnswer,
  AiHelpCategory,
  AiHelpConversationTurn,
  AiHelpPerspective,
  AiHelpQuestion,
  AiHelpTab,
} from '@components/ai-help/ai-help.model';

import { AiChatApiService } from '@services/api/ai-chat.service';
import { ProjectService } from '@services/api/project.service';
import { AiHelpService } from '@services/helper/ai-help.service';
import { UserPermissionService } from '@services/helper/user-permission.service';

/** A generic fallback answer when free text doesn't match any mocked question. */
function fallbackAnswer(related: readonly string[]): AiHelpAnswer {
  return {
    summary: "I couldn't find an exact match for that in the help content yet.",
    steps: [],
    tip: 'Try one of the suggested questions below, or contact support for anything urgent.',
    related: related.slice(0, 2).map((r) => r),
  };
}

/**
 * "AI Help" slide-out panel (FEC-1220).
 *
 * UI-only pass: the design is implemented in full against the approved
 * Zeplin screens (AI Help / A–E), but every answer is static mock content —
 * there is no AI backend behind it yet. Mounted once in the authenticated
 * shell; visibility is driven by {@link AiHelpService}. Its only entry point
 * is the floating "AI Help" button in its own template, shown on every page
 * while the panel is closed (FEC-1227 moved it out of the footer).
 */
@Component({
  selector: 'app-ai-help',
  imports: [
    A11yModule,
    CommonModule,
    FormsModule,
    AppIconComponent,
    DropdownMenuComponent,
    TextInputComponent,
  ],
  templateUrl: './ai-help.component.html',
  styleUrl: './ai-help.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiHelpComponent {
  protected readonly aiHelpService = inject(AiHelpService);
  private readonly router = inject(Router);
  private readonly userPermissionService = inject(UserPermissionService);
  private readonly aiChatApi = inject(AiChatApiService);
  private readonly projectService = inject(ProjectService);

  protected readonly open = this.aiHelpService.isOpen;

  /** Gates the slide transition until after first render — avoids a flash on mount. */
  protected readonly animationsReady = signal<boolean>(false);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /** "Page-aware" context — every main page has its own questions (see ai-help-data.ts). */
  protected readonly pageContext = computed(() => resolveAiHelpPageContext(this.currentUrl()));

  private readonly projectId = computed(() => aiHelpProjectId(this.currentUrl()));

  /**
   * The open project's name, for "You're viewing Apartments for 640 Bourke
   * Street" (Zeplin). Same query key as the project page, so it reads the
   * cached response rather than fetching again.
   */
  private readonly projectQuery = this.projectService.queryGetProjectById(
    () => this.projectId(),
    () => ({
      enabled: this.projectId() > 0,
      select: (response: unknown) => (response as { data?: { name?: string } })?.data?.name ?? '',
    }),
  );

  /** "Apartments for 640 Bourke Street" inside a project; the bare page label elsewhere. */
  protected readonly viewingLabel = computed(() => {
    const name = this.projectId() > 0 ? (this.projectQuery.data() as string | undefined) : '';
    const label = this.pageContext().label;
    return name ? `${label} for ${name}` : label;
  });

  protected readonly firstName = computed(
    () => this.userPermissionService.user()?.first_name || 'there',
  );

  protected readonly activeTab = signal<AiHelpTab>('ask');
  protected readonly askingAs = signal<AiHelpPerspective>('admin');
  protected readonly conversation = signal<readonly AiHelpConversationTurn[]>([]);
  protected readonly browseCategory = signal<AiHelpCategory | null>(null);
  protected readonly inputValue = signal<string>('');
  /** True while a data question is awaiting the AI backend (FEC-1227). */
  protected readonly thinking = signal<boolean>(false);

  protected readonly categories = AI_HELP_CATEGORIES;

  protected readonly tabs: readonly { id: AiHelpTab; label: string }[] = [
    { id: 'ask', label: 'Ask' },
    { id: 'browse', label: 'Browse' },
    { id: 'for-agents', label: 'For Agents' },
    { id: 'for-admins', label: 'For Admins' },
  ];

  protected readonly askingAsItems = computed<DropdownMenuItemType[]>(() => [
    { label: 'Admin', command: () => this.askingAs.set('admin') },
    { label: 'Agent', command: () => this.askingAs.set('agent') },
  ]);

  /** "On this page" — the page's own topic, then its mapped shared categories, for Browse. */
  protected readonly onThisPageCategories = computed<readonly AiHelpCategory[]>(() => {
    const context = this.pageContext();
    const ids = new Set(context.onThisPageCategoryIds);
    const shared = this.categories.filter((c) => ids.has(c.id));
    return context.pageCategory ? [context.pageCategory, ...shared] : shared;
  });
  protected readonly allTopicsCategories = computed<readonly AiHelpCategory[]>(() => {
    const ids = new Set(this.pageContext().onThisPageCategoryIds);
    return this.categories.filter((c) => !ids.has(c.id));
  });

  /** Content for the current tab's "home" state (before/between conversation turns). */
  protected readonly tabConfig = computed(() => {
    const context = this.pageContext();
    switch (this.activeTab()) {
      case 'for-agents':
        return {
          welcomeBody: `Showing how agents use ${context.label}. Answers will describe what an agent sees and can do, useful when you're supporting your agents.`,
          primaryLabel: `AGENTS ON ${context.label.toUpperCase()}`,
          primaryQuestions: context.agentsOnPage,
          secondaryLabel: context.agentCommonQuestions.length ? 'COMMON AGENT QUESTIONS' : null,
          secondaryQuestions: context.agentCommonQuestions,
          placeholder: 'Ask how an agent would…',
        };
      case 'for-admins':
        return {
          welcomeBody: `Showing how admins manage ${context.label}. Ask anything about setting up or editing this area.`,
          primaryLabel: `SUGGESTED FOR ${context.label.toUpperCase()}`,
          primaryQuestions: context.suggestedAdmin,
          secondaryLabel: null,
          secondaryQuestions: [],
          placeholder: `Ask anything about ${context.label}…`,
        };
      default:
        return {
          welcomeBody: `You're viewing ${this.viewingLabel()}. Ask me anything about this page, or pick a question below.`,
          primaryLabel: `SUGGESTED FOR ${context.label.toUpperCase()}`,
          primaryQuestions: context.suggestedAdmin,
          secondaryLabel: "FROM AN AGENT'S VIEW",
          secondaryQuestions: context.suggestedAgent,
          placeholder: `Ask anything about ${context.label}…`,
        };
    }
  });

  protected readonly hasConversation = computed(() => this.conversation().length > 0);

  /** Thumbs feedback per conversation turn, keyed by its index. */
  protected readonly feedback = signal<ReadonlyMap<number, 'up' | 'down'>>(new Map());

  /** Bottom input placeholder — follows conversation state, then the active tab/category. */
  protected readonly currentPlaceholder = computed(() => {
    if (this.hasConversation()) return 'Ask a follow-up…';
    if (this.activeTab() === 'browse') {
      const category = this.browseCategory();
      return category
        ? `Ask anything about ${category.label.toLowerCase()}…`
        : `Ask anything about ${this.pageContext().label}…`;
    }
    return this.tabConfig().placeholder;
  });

  constructor() {
    afterNextRender(() => this.animationsReady.set(true));
  }

  protected selectTab(tab: AiHelpTab): void {
    if (this.activeTab() === tab) return;
    this.activeTab.set(tab);
    this.askingAs.set(tab === 'for-agents' ? 'agent' : 'admin');
    this.conversation.set([]);
    this.browseCategory.set(null);
    this.inputValue.set('');
    this.feedback.set(new Map());
  }

  protected openCategory(category: AiHelpCategory): void {
    this.browseCategory.set(category);
  }

  protected backToCategories(): void {
    this.browseCategory.set(null);
  }

  protected askQuestion(question: AiHelpQuestion): void {
    this.pushTurn(question.text, question.answer);
  }

  protected submitFreeText(): void {
    const text = this.inputValue().trim();
    if (!text || this.thinking()) return;
    this.inputValue.set('');
    this.answerText(text);
  }

  protected askRelated(text: string): void {
    if (this.thinking()) return;
    this.answerText(text);
  }

  /**
   * Known how-to questions are answered from the static help content (free, no
   * tokens). Anything else is a data question and goes to the AI backend, with
   * the generic fallback if it is unavailable (off, rate-limited or erroring).
   */
  private answerText(text: string): void {
    const match = this.matchQuestion(text);
    if (match) {
      this.pushTurn(text, match.answer);
      return;
    }

    const history = this.conversation().flatMap((t) => [
      { role: 'user' as const, content: t.question },
      { role: 'assistant' as const, content: t.answer.summary },
    ]);
    const fallbackRelated = this.tabConfig().primaryQuestions.map((q) => q.text);

    this.thinking.set(true);
    this.aiChatApi.ask(text, history).subscribe({
      next: (answer) => {
        this.thinking.set(false);
        this.pushTurn(text, { summary: answer, steps: [], related: [] });
      },
      error: () => {
        this.thinking.set(false);
        this.pushTurn(text, fallbackAnswer(fallbackRelated));
      },
    });
  }

  private pushTurn(question: string, answer: AiHelpAnswer): void {
    const perspectiveLabel = this.askingAs() === 'agent' ? 'Agent steps' : 'Admin steps';
    this.conversation.update((turns) => [...turns, { question, answer, perspectiveLabel }]);
  }

  /** Best-effort free-text match — significant-word overlap against the mocked question set. */
  private matchQuestion(text: string): AiHelpQuestion | null {
    const words = new Set(text.toLowerCase().match(/[a-z]{3,}/g) ?? []);
    if (words.size === 0) return null;

    let best: { question: AiHelpQuestion; score: number } | null = null;
    for (const question of ALL_AI_HELP_QUESTIONS) {
      const qWords = question.text.toLowerCase().match(/[a-z]{3,}/g) ?? [];
      const score = qWords.filter((w) => words.has(w)).length;
      if (score >= 2 && (!best || score > best.score)) {
        best = { question, score };
      }
    }
    return best?.question ?? null;
  }

  protected setFeedback(index: number, value: 'up' | 'down'): void {
    this.feedback.update((current) => {
      const next = new Map(current);
      if (next.get(index) === value) next.delete(index);
      else next.set(index, value);
      return next;
    });
  }

  protected copyAnswer(answer: AiHelpAnswer): void {
    const text = [answer.summary, ...answer.steps.map((s) => `${s.title} — ${s.detail}`)].join(
      '\n',
    );
    navigator.clipboard?.writeText(text).catch(() => undefined);
  }

  /** Minimize — hides the panel but keeps the conversation for next time. */
  protected minimize(): void {
    this.aiHelpService.hide();
  }

  /** Close — ends the session: hides the panel and resets it back to Ask/Home. */
  protected close(): void {
    this.aiHelpService.hide();
    this.activeTab.set('ask');
    this.askingAs.set('admin');
    this.conversation.set([]);
    this.browseCategory.set(null);
    this.inputValue.set('');
    this.feedback.set(new Map());
  }

  protected trackByText(_index: number, item: AiHelpQuestion): string {
    return item.text;
  }

  protected trackByCategory(_index: number, item: AiHelpCategory): string {
    return item.id;
  }
}
